package org.vreo.devicehub.features.device.presentation.deviceManagement.messages

import androidx.compose.foundation.layout.Arrangement
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilledTonalButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.koin.compose.viewmodel.koinViewModel
import org.jetbrains.compose.resources.stringResource

import org.vreo.devicehub.features.core.presentation.components.ErrorDialog
import org.vreo.devicehub.features.core.presentation.components.LoadingDialog
import org.vreo.devicehub.features.core.domain.device.message.AnprCameraMessage
import org.vreo.devicehub.features.device.presentation.deviceManagement.messages.components.MessageDetails
import org.vreo.devicehub.features.device.presentation.deviceManagement.messages.components.MessageList

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.error_general_title
import devicehub.composeapp.generated.resources.message_list_load_more_button_title
import devicehub.composeapp.generated.resources.message_list_load_old_button_title
import devicehub.composeapp.generated.resources.message_list_title
import devicehub.composeapp.generated.resources.message_not_selected_title

/**
 * Message list screen root.
 *
 * @param viewModel view model for message list screen
 */
@Composable
fun MessageListScreenRoot(
    viewModel: MessageListViewModel = koinViewModel(),
) {
    val state: MessageListState by viewModel.state.collectAsStateWithLifecycle()

    MessageListView(
        state = state,
        onAction = { action ->
            viewModel.onAction(action)
        },
    )
}

/**
 * Message list view, displays list of messages and it's content.
 *
 * @param state state of message list screen
 * @param onAction action handler
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun MessageListView(
    state: MessageListState,
    onAction: (MessageListActions) -> Unit,
) {
    Box(
        contentAlignment = Alignment.Center,
        modifier = Modifier
            .fillMaxSize()
            .padding(16.dp)
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.fillMaxSize()
        ) {
            Text(
                text = stringResource(Res.string.message_list_title),
                style = MaterialTheme.typography.displaySmall.copy(
                    fontWeight = FontWeight.Bold
                )
            )

            // Video configuration
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier
                    .fillMaxWidth()
            ) {
                MessageListPanel(
                    messages = state.messages,
                    onAction = onAction,
                    modifier = Modifier
                        .weight(0.3f)
                        .fillMaxHeight()
                        .padding(16.dp)
                        .clip(RoundedCornerShape(8.dp))
                )

                Box(
                    contentAlignment = Alignment.Center,
                    modifier = Modifier
                        .weight(0.7f)
                        .fillMaxHeight()
                        .padding(8.dp)
                ) {
                    if (state.selectedMessage == null || !state.isDeviceActive) {
                        Text(
                            text = stringResource(Res.string.message_not_selected_title),
                            style = MaterialTheme.typography.displaySmall
                        )
                    } else {
                        MessageDetails(
                            deviceName = state.deviceName,
                            message = state.selectedMessage,
                            image = state.image,
                            modifier = Modifier
                                .fillMaxSize()
                        )
                    }
                }
            }

            // Conditional section for displaying loading and error states
            if (state.isLoading) {
                LoadingDialog()
            } else if (state.error != null) {
                ErrorDialog(
                    dialogTitle = stringResource(Res.string.error_general_title),
                    dialogText = state.error.asString(),
                    onDismissRequest = {
                        onAction(MessageListActions.OnClearError())
                    },
                    onConfirmation = {
                        onAction(MessageListActions.OnClearError())
                    }
                )
            }

        }
    }
}

/**
 * Message list panel. Displays message list.
 *
 * @param messages list of messages
 * @param onAction action handler
 */
@Composable
private fun MessageListPanel(
    messages: List<AnprCameraMessage>,
    onAction: (MessageListActions) -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
    ) {
        FilledTonalButton(
            onClick = { onAction(MessageListActions.OnUpdateMessageList()) },
            modifier = Modifier.fillMaxWidth().padding(8.dp)
        ) {
            Text(
                text = stringResource(Res.string.message_list_load_more_button_title),
                style = MaterialTheme.typography.labelSmall
            )
        }

        MessageList(
            messages = messages,
            onMessageClick = {
                onAction(MessageListActions.OnMessageClick(it))
            }, modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
                .padding(8.dp)
        )

        if (messages.isNotEmpty()) {
            FilledTonalButton(
                onClick = {
                    onAction(
                        MessageListActions.OnUpdateMessageList(
                            loadNew = false
                        )
                    )
                }, modifier = Modifier
                    .fillMaxWidth()
                    .padding(8.dp)
            ) {
                Text(
                    text = stringResource(Res.string.message_list_load_old_button_title),
                    style = MaterialTheme.typography.labelSmall
                )
            }
        }
    }
}

@Preview
@Composable
fun MessageListViewPreview() {
    @OptIn(ExperimentalTime::class) val currentMoment: Instant = Clock.System.now()

    @OptIn(ExperimentalTime::class) val datetimeInSystemZone: LocalDateTime =
        currentMoment.toLocalDateTime(TimeZone.currentSystemDefault())

    val messageDemoList = mutableListOf<AnprCameraMessage>()
    for (i in 0..10) {
        messageDemoList.add(
            AnprCameraMessage(
                msgId = i.toString(),
                softwareVersion = "1.0.0",
                ocrVersion = "1.0.0",
                vehicleId = 1,
                licensePlate = "abc 123",
                licensePlateWidth = 1,
                licensePlateHeight = 1,
                confidenceScore = 1f,
                ocrAttempt = 1,
                imageUrl = "/example/url",
                date = datetimeInSystemZone,
                timeStamp = 0,
                selected = false
            )
        )
    }

    MaterialTheme {
        MessageListView(
            state = MessageListState(
                messages = messageDemoList
            ),
            onAction = {},
        )
    }
}